import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment-jalaali";
import { Calendar, Trash2, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, Loader2, Lock } from "lucide-react";
import { toast } from "react-toastify";
import useAxiosAdmin from "../lib/axiosAdmin";

const AdminHolidaysContent = () => {
  const axiosAdmin = useAxiosAdmin();
  const serverTimeData = useSelector((state) => state.info.serverTime);
  
  const serverTimeISO = serverTimeData?.serverTime || new Date().toISOString();
  
  const currentJalaaliYear = moment(serverTimeISO).jYear();
  const currentJalaaliMonth = moment(serverTimeISO).jMonth() + 1;
  const currentJalaaliDay = moment(serverTimeISO).jDate();
  
  const [selectedYear, setSelectedYear] = useState(currentJalaaliYear);
  const [selectedMonth, setSelectedMonth] = useState(currentJalaaliMonth);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const persianMonths = [
    { id: 1, name: "فروردین", days: 31 },
    { id: 2, name: "اردیبهشت", days: 31 },
    { id: 3, name: "خرداد", days: 31 },
    { id: 4, name: "تیر", days: 31 },
    { id: 5, name: "مرداد", days: 31 },
    { id: 6, name: "شهریور", days: 31 },
    { id: 7, name: "مهر", days: 30 },
    { id: 8, name: "آبان", days: 30 },
    { id: 9, name: "آذر", days: 30 },
    { id: 10, name: "دی", days: 30 },
    { id: 11, name: "بهمن", days: 30 },
    { id: 12, name: "اسفند", days: 29 },
  ];

  const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

  const isLeapYear = (year) => {
    const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
    return breaks.includes(year % 33);
  };

  const getDaysInMonth = (month, year) => {
    if (!month || month < 1 || month > 12) return 30;
    if (month === 12 && isLeapYear(year)) return 30;
    return persianMonths[month - 1].days;
  };

  // 🔥 چک کردن اینکه آیا تاریخ در گذشته است
  const isDateInPast = (jYear, jMonth, jDay) => {
    const dateToCheck = moment(`${jYear}/${jMonth}/${jDay}`, "jYYYY/jM/jD");
    const today = moment(serverTimeISO);
    return dateToCheck.isBefore(today, "day");
  };

  // 🔥 دریافت تعطیلات از سرور
  const fetchHolidays = async () => {
    try {
      setLoading(true);
      
      // تبدیل سال شمسی به میلادی
      const gregorianYear = moment(`${selectedYear}/01/01`, "jYYYY/jMM/jDD").year();
      
      const res = await axiosAdmin.get(`/api/admin/holidays?year=${gregorianYear}`);
      
      console.log(res);

      if (res.data?.success && res.data?.data) {
        setHolidays(res.data.data.dates || []);
      } else {
        setHolidays([]);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  // 🔥 ذخیره تعطیلات در سرور
  const saveHolidays = async (updatedHolidays) => {
    try {
      setSaving(true);
      
      // تبدیل سال شمسی به میلادی برای ذخیره
      const gregorianYear = moment(`${selectedYear}/01/01`, "jYYYY/jMM/jDD").year();
      
      await axiosAdmin.post("/api/admin/holidays", {
        year: gregorianYear,
        dates: updatedHolidays,
      });

      setHolidays(updatedHolidays);
      
    } catch (error) {
      console.error("Error saving holidays:", error);
      toast.error("خطا در ذخیره تعطیلات");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const generateCalendarDays = () => {
    if (!selectedMonth || !selectedYear) return [];

    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
    const startDayOfWeek = firstDayOfMonth.day() + 1;

    const days = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD");
      const gregorianDate = jalaaliDate.format("YYYY-MM-DD");

      days.push({
        day,
        jalaaliDate: jalaaliDate.format("jYYYY/jMM/jDD"),
        // noon UTC مانع نمایش روز بعد در timezoneهای مختلف می‌شود.
        gregorianDate: `${gregorianDate}T12:00:00.000Z`,
        isToday: jalaaliDate.isSame(moment(serverTimeISO), "day"),
        isPast: isDateInPast(selectedYear, selectedMonth, day),
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const isDateSelected = (gregorianDate) => {
    return holidays?.some(date => {
      // مقایسه فقط بخش تاریخ (بدون ساعت)
      const dateOnly = date.split('T')[0];
      const gregorianDateOnly = gregorianDate.split('T')[0];
      return dateOnly === gregorianDateOnly;
    }) || false;
  };

  const toggleDateSelection = async (dateObj) => {
    if (!dateObj || saving) return;
    const { gregorianDate, jalaaliDate, isPast } = dateObj;

    // 🔥 جلوگیری از تغییر تاریخ‌های گذشته
    if (isPast) {
      toast.warning("امکان تغییر تعطیلات گذشته وجود ندارد");
      return;
    }

    if (isDateSelected(gregorianDate)) {
      setConfirmDelete({ gregorianDate, jalaaliDate });
    } else {
      const updatedHolidays = [...holidays, gregorianDate].sort();
      
      try {
        await saveHolidays(updatedHolidays);
        toast.success(`${jalaaliDate} به تعطیلات عمومی اضافه شد`);
      } catch (error) {
        // خطا در toast نمایش داده شده
      }
    }
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete || saving) return;
    const { gregorianDate, jalaaliDate } = confirmDelete;

    const updatedHolidays = holidays.filter((date) => {
      const dateOnly = date.split('T')[0];
      const gregorianDateOnly = gregorianDate.split('T')[0];
      return dateOnly !== gregorianDateOnly;
    });

    try {
      await saveHolidays(updatedHolidays);
      toast.success(`${jalaaliDate} از تعطیلات عمومی حذف شد`);
      setConfirmDelete(null);
    } catch (error) {
      // خطا در toast نمایش داده شده
    }
  };

  const changeMonth = (direction) => {
    if (direction === "next") {
      if (selectedMonth === 12) { 
        setSelectedMonth(1); 
        setSelectedYear(selectedYear + 1); 
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    } else {
      if (selectedMonth === 1) { 
        setSelectedMonth(12); 
        setSelectedYear(selectedYear - 1); 
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    }
  };

  const getSelectedDatesInPersian = () => {
    if (!holidays?.length) return [];
    
    return holidays
      .map((gregorianDate) => {
        const jDate = moment(gregorianDate).format("jYYYY/jMM/jDD");
        return {
          jDate,
          gregorianDate,
        };
      })
      .sort((a, b) => a.jDate.localeCompare(b.jDate));
  };

  // 🔥 فیلتر کردن تعطیلات فقط برای ماه جاری
  const getCurrentMonthHolidays = () => {
    if (!holidays?.length) return [];
    
    return holidays
      .filter((gregorianDate) => {
        const jDate = moment(gregorianDate);
        return jDate.jYear() === selectedYear && (jDate.jMonth() + 1) === selectedMonth;
      })
      .map((gregorianDate) => ({
        jDate: moment(gregorianDate).format("jYYYY/jMM/jDD"),
        gregorianDate,
      }))
      .sort((a, b) => a.jDate.localeCompare(b.jDate));
  };

  const currentMonthName =
    selectedMonth >= 1 && selectedMonth <= 12
      ? persianMonths[selectedMonth - 1].name
      : "نامشخص";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-3" />
          <p className="text-slate-600">در حال بارگذاری تعطیلات...</p>
        </div>
      </div>
    );
  }

  const currentMonthHolidays = getCurrentMonthHolidays();

  return (
    <div className="space-y-6 xm:p-6">
      {/* راهنما */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">راهنمای تنظیم تعطیلات عمومی سالن:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>روی هر روز کلیک کنید تا به عنوان تعطیلی عمومی ثبت شود</li>
            <li>روزهای انتخاب شده با رنگ قرمز مشخص می‌شوند</li>
            <li>برای حذف، دوباره روی روز کلیک کنید</li>
            <li>تغییرات به صورت خودکار در سرور ذخیره می‌شوند</li>
            <li>تعطیلات عمومی برای همه کارکنان و خدمات اعمال می‌شود</li>
            <li className="text-orange-700 font-medium">⚠️ فقط امکان تغییر روزهای آینده وجود دارد</li>
          </ul>
        </div>
      </div>

      {/* هدر تقویم */}
      <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-5 shadow-lg shadow-purple-500/25">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => changeMonth("prev")}
            disabled={saving}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
          </button>

          <div className="text-center">
            <h3 className="text-2xl font-bold text-white">
              {currentMonthName} {selectedYear}
            </h3>
            <p className="text-purple-100 text-sm mt-1">
              {isLeapYear(selectedYear) ? "سال کبیسه" : "سال عادی"}
            </p>
            {saving && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <Loader2 className="w-4 h-4 text-white animate-spin" />
                <span className="text-white/90 text-xs">در حال ذخیره...</span>
              </div>
            )}
          </div>

          <button
            onClick={() => changeMonth("next")}
            disabled={saving}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day, index) => (
            <div key={index} className="text-center text-white/90 font-semibold text-sm py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) return <div key={`empty-${index}`} className="aspect-square" />;

            const isSelected = isDateSelected(dateObj.gregorianDate);
            const isToday = dateObj.isToday;
            const isPast = dateObj.isPast;

            return (
              <button
                key={index}
                onClick={() => toggleDateSelection(dateObj)}
                disabled={saving || isPast}
                className={`
                  aspect-square rounded-lg font-medium text-sm transition-all duration-300 relative
                  disabled:cursor-not-allowed
                  ${isPast && !isSelected
                    ? "bg-white/5 text-white/30 cursor-not-allowed"
                    : isSelected
                    ? "bg-red-500 text-white shadow-lg shadow-red-500/50 scale-105"
                    : isToday
                    ? "bg-white/20 text-white border-2 border-white/50"
                    : "bg-white/10 text-white hover:bg-white/20 hover:scale-105"
                  }
                  ${isPast && isSelected ? "opacity-70" : ""}
                `}
              >
                {dateObj.day}
                {isPast && !isSelected && (
                  <Lock className="w-3 h-3 absolute top-1 right-1 text-white/20" />
                )}
                {isToday && !isSelected && !isPast && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* لیست تعطیلات ماه جاری */}
      {currentMonthHolidays && currentMonthHolidays.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            تعطیلات {currentMonthName} {selectedYear} ({currentMonthHolidays.length} روز)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {currentMonthHolidays.map(({ jDate, gregorianDate }, index) => {
              const isPastDate = moment(gregorianDate).isBefore(moment(serverTimeISO), "day");
              
              return (
                <div
                  key={index}
                  className={`bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between group hover:shadow-md transition-shadow duration-300 ${
                    isPastDate ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                      {isPastDate ? (
                        <Lock className="w-4 h-4 text-white" />
                      ) : (
                        <Calendar className="w-4 h-4 text-white" />
                      )}
                    </div>
                      <span className="text-sm font-medium text-slate-700 font-mono">
                        {moment(gregorianDate).format("jYYYY/jMM/jDD")}
                      </span>
                  </div>
                  {!isPastDate && (
                    <button
                      onClick={() => setConfirmDelete({ gregorianDate, jalaaliDate: moment(gregorianDate).format("jYYYY/jMM/jDD") })}
                      disabled={saving}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-100 transition-all duration-300 sm:opacity-0 group-hover:opacity-100 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">هنوز تعطیلی عمومی برای {currentMonthName} {selectedYear} ثبت نشده است</p>
          <p className="text-slate-400 text-xs mt-1">روی روزهای تقویم کلیک کنید تا به عنوان تعطیلی عمومی ثبت شود</p>
        </div>
      )}

      {/* مودال تایید حذف */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-300">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 text-center mb-2">حذف تعطیلی عمومی</h3>
            <p className="text-sm text-slate-600 text-center mb-6">
              آیا از حذف تاریخ{" "}
              <span className="font-bold text-slate-800 font-mono">{confirmDelete.jalaaliDate}</span>{" "}
              از لیست تعطیلات عمومی سالن اطمینان دارید؟
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                انصراف
              </button>
              <button
                onClick={confirmDeleteAction}
                disabled={saving}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-md shadow-red-500/25 hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    در حال حذف...
                  </>
                ) : (
                  "حذف کن"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHolidaysContent;
